import React, { useState } from "react";
import AppointmentService, {
  Appointment,
} from "../../services/appointmentService";
import ServiceSelectionModal from "./ServiceSelectionModal";
import { useAuth } from "../../hooks/useAuth";

interface SelectedService {
  service: {
    id: number;
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
  };
  referrer_name: string;
  customPrice?: number; // ADD THIS
  pricingReason?: string; // ADD THIS
}

interface AppointmentStatusMenuProps {
  appointment: Appointment;
  position: { x: number; y: number };
  onClose: () => void;
  onViewDetails: (appointment: Appointment) => void;
  onStatusChange: (
    action: "confirm" | "arrive" | "complete" | "cancel" | "no_show" | "delete",
    appointmentId: number,
    reason?: string,
    services?: SelectedService[]
  ) => Promise<void>;
  onSaveCustomPricing?: (
    appointmentId: number,
    services: SelectedService[]
  ) => Promise<void>; // ADD THIS
  onBookAppointment?: (prefilledPatient?: {
    id: number;
    name: string;
    phone: string;
  }) => void;
  doctors?: any[];
  patients?: any[];
}

const AppointmentStatusMenu: React.FC<AppointmentStatusMenuProps> = ({
  appointment,
  position,
  onClose,
  onViewDetails,
  onStatusChange,
  onSaveCustomPricing,
  onBookAppointment,

  doctors = [],
  patients = [],
}) => {
  const { isSecretary } = useAuth();
  const [showCancelInput, setShowCancelInput] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // FIXED: Update state type to handle mode
  const [showServiceSelection, setShowServiceSelection] = useState<{
    appointment: Appointment;
    mode: "pricing" | "completion" | "editing"; // ADD "editing"
  } | null>(null);
  const calculateMenuPosition = (
    clickX: number,
    clickY: number,
    menuWidth: number = 200,
    menuHeight: number = 300
  ): { x: number; y: number } => {
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    const padding = 16; // Padding from screen edges

    let x = clickX;
    let y = clickY;

    // Adjust horizontal position
    if (x + menuWidth + padding > viewport.width) {
      x = viewport.width - menuWidth - padding;
    }
    if (x < padding) {
      x = padding;
    }

    // Adjust vertical position
    if (y + menuHeight + padding > viewport.height) {
      y = viewport.height - menuHeight - padding;
    }
    if (y < padding) {
      y = padding;
    }

    // Special mobile handling - center horizontally if very small screen
    if (viewport.width <= 480) {
      x = Math.max(padding, (viewport.width - menuWidth) / 2);
    }

    return { x, y };
  };
  const handleBookAppointment = () => {
    if (onBookAppointment && appointment.patient) {
      // Pre-fill with current patient's information
      const prefilledPatient = {
        id: appointment.patient.id,
        name: appointment.patient.full_name,
        phone: appointment.patient.phone,
      };
      onBookAppointment(prefilledPatient);
    }
    onClose();
  };
  const isMobile = window.innerWidth <= 768;
  const adjustedPosition = calculateMenuPosition(
    position.x,
    position.y,
    isMobile ? 280 : 200, // Wider menu on mobile for better touch targets
    400 // Estimated menu height
  );
  // ADD BACK THE MISSING STYLES
  const menuStyle: React.CSSProperties = {
    position: "fixed",
    top: adjustedPosition.y,
    left: adjustedPosition.x,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: isMobile ? "12px" : "8px", // Larger radius on mobile
    boxShadow: isMobile
      ? "0 20px 40px rgba(0, 0, 0, 0.25)"
      : "0 10px 25px rgba(0, 0, 0, 0.15)",
    zIndex: 1000,
    minWidth: isMobile ? "280px" : "200px", // Wider on mobile
    maxWidth: isMobile ? "calc(100vw - 32px)" : "300px", // Respect screen bounds
    padding: isMobile ? "12px" : "8px",
    // Mobile-specific optimizations
    ...(isMobile && {
      maxHeight: "calc(100vh - 100px)", // Prevent menu from being too tall
      overflowY: "auto", // Enable scrolling if needed
      fontSize: "16px", // Prevent zoom on iOS
    }),
  };

  const buttonStyle = (
    variant: "primary" | "success" | "danger" | "secondary" = "secondary"
  ): React.CSSProperties => {
    const variants = {
      primary: { bg: "#3b82f6", text: "#ffffff" },
      success: { bg: "#10b981", text: "#ffffff" },
      danger: { bg: "#ef4444", text: "#ffffff" },
      secondary: { bg: "#f3f4f6", text: "#374151" },
    };
    const isMobile = window.innerWidth <= 768;
    const adjustedPosition = calculateMenuPosition(
      position.x,
      position.y,
      isMobile ? 280 : 200, // Wider menu on mobile for better touch targets
      400 // Estimated menu height
    );
    const colors = variants[variant];

    return {
      width: "100%",
      padding: isMobile ? "12px 16px" : "8px 12px", // Larger touch targets on mobile
      fontSize: isMobile ? "16px" : "13px", // Prevent iOS zoom
      fontWeight: "500",
      border: "none",
      borderRadius: isMobile ? "8px" : "6px",
      cursor: "pointer",
      background: colors.bg,
      color: colors.text,
      marginBottom: isMobile ? "8px" : "4px", // More spacing on mobile
      transition: "all 0.2s ease",
      textAlign: "left",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      minHeight: isMobile ? "48px" : "auto", // Minimum touch target size
      // Prevent text selection on touch devices
      userSelect: "none",
      WebkitUserSelect: "none",
      WebkitTapHighlightColor: "transparent",
    };
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "12px 14px" : "8px 10px",
    fontSize: isMobile ? "16px" : "13px", // Prevent iOS zoom
    border: "1px solid #d1d5db",
    borderRadius: isMobile ? "8px" : "4px",
    marginBottom: isMobile ? "12px" : "8px",
    minHeight: isMobile ? "44px" : "auto", // Minimum touch target
    WebkitAppearance: "none", // Reset iOS styling
  };
  const backdropStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
    backgroundColor: isMobile ? "rgba(0, 0, 0, 0.3)" : "transparent", // Slight overlay on mobile
  };

  // Alternative: Mobile bottom sheet approach (optional)
  const mobileBottomSheetStyle: React.CSSProperties = {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    background: "#ffffff",
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    boxShadow: "0 -10px 30px rgba(0, 0, 0, 0.2)",
    zIndex: 1000,
    padding: "20px",
    maxHeight: "80vh",
    overflowY: "auto",
    transform: "translateY(0)",
    transition: "transform 0.3s ease",
  };
  const handleCancel = () => {
    onStatusChange("cancel", appointment.id, cancelReason);
    onClose();
  };

  const handleDelete = () => {
    onStatusChange("delete", appointment.id);
    onClose();
  };
  // ADD THESE TWO HANDLERS:
  const handleEditCompleted = async (
    appointmentId: number,
    services: SelectedService[],
    password: string,
    editReason: string
  ) => {
    try {
      await AppointmentService.editCompletedAppointment(appointmentId, {
        password,
        services: services.map((service) => ({
          service_id: service.service.id,
          custom_price: service.customPrice,
          pricing_notes: service.pricingReason,
          referrer_name: service.referrer_name,
        })),
        edit_reason: editReason,
      });

      setShowServiceSelection(null);
      onClose();
      // Optionally trigger a refresh here
    } catch (error) {
      console.error("Error editing completed appointment:", error);
      throw error;
    }
  };

  const handleMarkIncomplete = async (
    appointmentId: number,
    password: string,
    reason: string
  ) => {
    try {
      await AppointmentService.markAppointmentIncomplete(appointmentId, {
        password,
        reason,
      });

      setShowServiceSelection(null);
      onClose();
      // Optionally trigger a refresh here
    } catch (error) {
      console.error("Error marking appointment incomplete:", error);
      throw error;
    }
  };
  // Handle completion with services
  const handleCompleteWithServices = async (
    selectedServices: SelectedService[]
  ) => {
    try {
      await onStatusChange(
        "complete",
        appointment.id,
        undefined,
        selectedServices
      );
      setShowServiceSelection(null); // FIXED: Set to null
      onClose();
    } catch (error) {
      console.error("Error completing appointment with services:", error);
      throw error;
    }
  };

  // ADD: Handle saving custom pricing
  const handleSaveCustomPricing = async (
    selectedServices: SelectedService[]
  ) => {
    try {
      if (onSaveCustomPricing) {
        await onSaveCustomPricing(appointment.id, selectedServices);
      }
      setShowServiceSelection(null);
      onClose();
    } catch (error) {
      console.error("Error saving custom pricing:", error);
      throw error;
    }
  };

  // Handle completion with services
  const handleCompleteWithServicesClick = () => {
    setShowServiceSelection({ appointment, mode: "completion" }); // FIXED
  };

  // Handle view details click
  const handleViewDetails = () => {
    onClose();
    onViewDetails(appointment);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999,
          backgroundColor: isMobile ? "rgba(0, 0, 0, 0.3)" : "transparent",
        }}
        onClick={onClose}
      />

      {/* Menu */}
      <div style={menuStyle}>
        <div
          style={{
            padding: isMobile ? "12px 16px" : "8px 12px",
            borderBottom: "1px solid #f3f4f6",
            marginBottom: isMobile ? "12px" : "8px",
            fontSize: isMobile ? "16px" : "14px",
            fontWeight: "600",
            color: "#1f2937",
          }}
        >
          {appointment.patient?.first_name} {appointment.patient?.last_name}
        </div>

        {/* Status Actions */}
        {appointment.status === "scheduled" && (
          <button
            onClick={() => {
              onStatusChange("confirm", appointment.id);
              onClose();
            }}
            style={buttonStyle("success")}
          >
            <span>✓</span> Confirm Appointment
          </button>
        )}

        {/* Set Custom Price Button */}
        {!isSecretary() &&
          (appointment.status === "confirmed" ||
            appointment.status === "scheduled" ||
            appointment.status === "arrived") && (
            <button
              onClick={() => {
                setShowServiceSelection({ appointment, mode: "pricing" });
              }}
              style={buttonStyle("secondary")}
            >
              <span>💲</span> Set Custom Price
            </button>
          )}

        {/* Complete Options */}
        {(appointment.status === "confirmed" ||
          appointment.status === "scheduled" ||
          appointment.status === "arrived") && (
          <button
            onClick={handleCompleteWithServicesClick}
            style={buttonStyle("primary")}
          >
            <span>💰</span> Complete with Services
          </button>
        )}

        {appointment.status === "completed" && (
          <>
            <button
              onClick={() => {
                setShowServiceSelection({ appointment, mode: "editing" });
              }}
              style={buttonStyle("secondary")}
            >
              <span>✏️</span> Edit Completed
            </button>

            <hr
              style={{
                margin: isMobile ? "12px 0" : "8px 0",
                border: "none",
                borderTop: "1px solid #f3f4f6",
              }}
            />
          </>
        )}

        {/* Book Another Appointment Button - Always show when prop is provided */}
        {onBookAppointment && (
          <button
            onClick={handleBookAppointment}
            style={buttonStyle("primary")}
          >
            <span>📅</span> Book Another Appointment
          </button>
        )}

        {appointment.can_be_cancelled && !showCancelInput && (
          <button
            onClick={() => setShowCancelInput(true)}
            style={buttonStyle("danger")}
          >
            <span>✖</span> Cancel Appointment
          </button>
        )}

        {/* Cancel Input */}
        {showCancelInput && !showDeleteConfirm && (
          <div style={{ padding: isMobile ? "12px 0" : "8px 0" }}>
            <input
              type="text"
              id="cancellation_reason"
              name="cancellation_reason"
              placeholder="Cancellation reason (optional)"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              style={inputStyle}
              autoFocus
            />

            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={handleCancel}
                style={{
                  ...buttonStyle("danger"),
                  fontSize: isMobile ? "14px" : "12px",
                  padding: isMobile ? "8px 12px" : "6px 10px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setShowCancelInput(false)}
                style={{
                  ...buttonStyle("secondary"),
                  fontSize: isMobile ? "14px" : "12px",
                  padding: isMobile ? "8px 12px" : "6px 10px",
                }}
              >
                Back
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {showDeleteConfirm && !showCancelInput && (
          <div style={{ padding: isMobile ? "12px 0" : "8px 0" }}>
            <div
              style={{
                padding: isMobile ? "12px 16px" : "8px 12px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: isMobile ? "8px" : "4px",
                fontSize: isMobile ? "14px" : "12px",
                color: "#dc2626",
                marginBottom: isMobile ? "12px" : "8px",
                textAlign: "center",
              }}
            >
              ⚠️ This will permanently delete the appointment
            </div>
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={handleDelete}
                style={{
                  ...buttonStyle("danger"),
                  fontSize: isMobile ? "14px" : "12px",
                  padding: isMobile ? "8px 12px" : "6px 10px",
                }}
              >
                🗑️ Delete
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                style={{
                  ...buttonStyle("secondary"),
                  fontSize: isMobile ? "14px" : "12px",
                  padding: isMobile ? "8px 12px" : "6px 10px",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Delete Button */}
        {!showCancelInput && !showDeleteConfirm && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={buttonStyle("danger")}
          >
            <span>🗑️</span> Delete Appointment
          </button>
        )}

        <hr
          style={{
            margin: isMobile ? "12px 0" : "8px 0",
            border: "none",
            borderTop: "1px solid #f3f4f6",
          }}
        />

        {/* View Details Button */}
        <button onClick={handleViewDetails} style={buttonStyle("secondary")}>
          <span>👁</span> View Details
        </button>
      </div>

      {/* Service Selection Modal */}
      {showServiceSelection && (
        <ServiceSelectionModal
          isOpen={!!showServiceSelection}
          appointment={showServiceSelection.appointment}
          mode={showServiceSelection.mode}
          onClose={() => setShowServiceSelection(null)}
          onComplete={handleCompleteWithServices}
          onSaveCustomPricing={onSaveCustomPricing}
          onEditCompleted={handleEditCompleted}
          onMarkIncomplete={handleMarkIncomplete}
        />
      )}
    </>
  );
};

export default AppointmentStatusMenu;
